import { inject, Service, signal } from '@angular/core'
import { ValidationErrors } from '@angular/forms'
import { firstValueFrom, map, Observable } from 'rxjs'
import { Control } from 'superlikers-dynamic-forms'
import { LoginResponse, NotificationChannel, UpdateParticipantResponse } from '@superlikers/microsites-api'

import { User } from '@core/data/models'
import { CacheService } from '@core/persistence'
import { environment } from '@environments/environment'
import { AuthApi } from '../auth-api/auth-api'
import { AuthRequirementsPoller } from '../auth-requirements-poller/auth-requirements-poller'
import { AuthError } from '../models'

@Service()
export class AuthService {
  #authApi = inject(AuthApi)
  #cacheService = inject(CacheService)
  #requirementsPoller = inject(AuthRequirementsPoller)

  readonly currentUser = signal<User | null>(null)

  /** La raíz deja de pintar la app mientras el logout responde y recarga. */
  readonly isLoggingOut = signal(false)

  /** Los guards la llaman de forma perezosa; cubre también el refresco. */
  async initialize(): Promise<void> {
    const user = await firstValueFrom(this.#authApi.fetchCurrentUser())
    this.currentUser.set(user)

    if (user) {
      await this.#cacheService.init(user)
      this.#requirementsPoller.start()
    }
  }

  getLoginControls(): Promise<Control[]> {
    return firstValueFrom(this.#authApi.getLoginControls())
  }

  getSignupControls(): Promise<Control[]> {
    return firstValueFrom(this.#authApi.getSignupControls())
  }

  /** Usa `updateParticipant`, no `signup`. Valida `response.success`. */
  async completeSignup(data: Record<string, unknown>): Promise<void> {
    const response = await firstValueFrom(this.#authApi.updateParticipant(data))

    if (!response.success) {
      throw new Error(response.message || 'No se pudo completar el registro')
    }

    await this.initialize()
  }

  /** Deja la sesión lista ANTES de resolver. */
  async login(credentials: Record<string, string>): Promise<LoginResponse> {
    try {
      const response = await firstValueFrom(this.#authApi.login(credentials))
      await this.initialize()
      return response
    } catch (err) {
      const httpError = err as { error?: AuthError }
      throw httpError.error ?? err
    }
  }

  async logout(): Promise<void> {
    this.isLoggingOut.set(true)

    try {
      await firstValueFrom(this.#authApi.logout())
    } finally {
      this.#requirementsPoller.stop()
      this.currentUser.set(null)
      window.location.reload()
    }
  }

  async loadCurrentUser(): Promise<User | null> {
    const user = await firstValueFrom(this.#authApi.fetchCurrentUser())
    this.currentUser.set(user)

    return user
  }

  updateCurrentUser(updates: Record<string, unknown>): Promise<UpdateParticipantResponse> {
    return firstValueFrom(this.#authApi.updateParticipant(updates))
  }

  isAuthenticated(): boolean {
    return this.currentUser() !== null
  }

  validateChannel(channel: NotificationChannel, value: string): Observable<ValidationErrors | null> {
    return this.#authApi.validateChannel(channel, value).pipe(map(response => (response.state === 'error' ? { custom: response.message } : null)))
  }

  /** La URL de retorno se arma con el `id` del micrositio. */
  async sendPasswordResetToken(data: Record<string, string>): Promise<void> {
    const redirectUrl = `${environment.id}/password/edit`
    const response = await firstValueFrom(this.#authApi.sendToken('email', data, redirectUrl))

    if (response.state !== 'success') {
      throw new Error(response.message ?? 'Error al enviar el correo de recuperación')
    }
  }

  async updatePassword(password: string, passwordConfirmation: string, token: string, channel: NotificationChannel = 'email'): Promise<void> {
    const response = await firstValueFrom(this.#authApi.updatePassword(password, passwordConfirmation, token, channel))

    if (response.state !== 'success') {
      throw new Error(response.message ?? 'No se pudo actualizar la contraseña')
    }
  }
}
